import React, { useState, useEffect } from 'react';
import {
  Row, Col, Card, Select, Typography, Tag, Space, Button, Badge, Table, Upload, message,
  Steps, Alert, Statistic, Result, Input,
} from 'antd';
import {
  TagsOutlined, UploadOutlined, FileExcelOutlined, CheckCircleOutlined, WarningOutlined,
  CloudUploadOutlined, ApiOutlined, RollbackOutlined, SearchOutlined,
} from '@ant-design/icons';
import { getEvents, getCategories, provisionTags, decodeEpc } from '../api';
import * as ExcelJS from 'exceljs';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Step } = Steps;

const PRIMARY = '#0F1D6B';
const TEAL    = '#00838F';

// Validate EPC: hex string of length 8-32 (32-128 bits)
const isValidEpc = (s) => typeof s === 'string' && /^[0-9A-Fa-f]{8,32}$/.test(s.trim());

export default function TagProvisioning() {
  const [step, setStep] = useState(0);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState([]);
  const [filename, setFilename] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // EPC decoder
  const [decodeInput, setDecodeInput] = useState('');
  const [decoded, setDecoded] = useState(null);
  const [decodeBusy, setDecodeBusy] = useState(false);

  useEffect(() => {
    getEvents().then(r => {
      const ev = r.data.data || [];
      setEvents(ev);
      const a = ev.find(e => e.status === 'active') || ev[0];
      if (a) setEventId(a.id);
    }).catch(() => {});
    getCategories().then(r => setCategories(r.data.data || [])).catch(() => {});
  }, []);

  const parseFile = async (file) => {
    setFilename(file.name);
    try {
      const buf = await file.arrayBuffer();
      let parsed = [];
      if (file.name.toLowerCase().endsWith('.csv')) {
        const text = new TextDecoder().decode(buf);
        const lines = text.split(/\r?\n/).filter(Boolean);
        const header = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/^"|"$/g, ''));
        parsed = lines.slice(1).map((line, idx) => {
          const cols = parseCsvLine(line);
          const o = { _row: idx + 2 };
          header.forEach((h, i) => o[h] = cols[i] || '');
          return o;
        });
      } else {
        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(buf);
        const ws = wb.worksheets[0];
        const headerRow = ws.getRow(1);
        const headers = [];
        headerRow.eachCell({ includeEmpty: false }, c => headers.push(String(c.value || '').trim().toLowerCase()));
        ws.eachRow((row, rowIdx) => {
          if (rowIdx === 1) return;
          const o = { _row: rowIdx };
          headers.forEach((h, i) => {
            const c = row.getCell(i + 1).value;
            o[h] = c == null ? '' : (typeof c === 'object' && c.text) ? c.text : String(c);
          });
          parsed.push(o);
        });
      }

      // Normalize column names: accept "rfid_tag", "epc", "tag", "delegate_name" or "name"
      const normalised = parsed.map(r => {
        const tag = r.rfid_tag || r.epc || r.tag || '';
        const name = r.delegate_name || r.name || r.full_name || '';
        const valid = isValidEpc(tag) && !!name;
        return {
          _row:          r._row,
          rfid_tag:      tag.trim().toUpperCase(),
          delegate_name: name.trim(),
          category:      r.category || '',
          company:       r.company || '',
          email:         r.email || '',
          phone:         r.phone || '',
          designation:   r.designation || '',
          valid,
          error:         !tag ? 'Missing tag' :
                         !isValidEpc(tag) ? 'Invalid EPC (8-32 hex chars)' :
                         !name ? 'Missing name' : null,
        };
      });
      setRows(normalised);
      setStep(1);
      message.success(`Parsed ${normalised.length} rows from ${file.name}`);
    } catch (err) {
      message.error('Failed to parse file: ' + err.message);
    }
    return false; // prevent upload
  };

  const submit = async () => {
    if (!eventId) { message.error('Select an event'); return; }
    const valid = rows.filter(r => r.valid);
    if (valid.length === 0) { message.error('No valid rows to import'); return; }
    setSubmitting(true);
    try {
      const tags = valid.map(r => {
        const cat = categories.find(c => (c.name || '').toLowerCase() === (r.category || '').toLowerCase());
        return {
          rfid_tag:      r.rfid_tag,
          delegate_name: r.delegate_name,
          category_id:   cat?.id || null,
          company:       r.company,
          email:         r.email,
          phone:         r.phone,
          designation:   r.designation,
        };
      });
      const res = await provisionTags({ event_id: eventId, tags });
      setResult(res.data);
      setStep(2);
    } catch (err) {
      message.error(err?.response?.data?.message || 'Provisioning failed');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setRows([]); setFilename(''); setResult(null); setStep(0);
  };

  const downloadTemplate = () => {
    const csv = 'rfid_tag,delegate_name,category,company,email,phone,designation\n' +
                'E28011700000020D2D2A1234,Rajesh Kumar,VIP,Identium Tech,raj@identium.com,9876543210,CEO\n' +
                'E2801170000002034A8B9CDE,Priya Sharma,Speaker,Tech Mahindra,priya@tm.com,9988776655,VP Engineering\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'tag_provisioning_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleDecode = async () => {
    if (!decodeInput) return;
    setDecodeBusy(true);
    try {
      const r = await decodeEpc({ epc: decodeInput });
      setDecoded(r.data.data);
    } catch (err) {
      setDecoded({ error: err?.response?.data?.message || 'Invalid EPC' });
    } finally {
      setDecodeBusy(false);
    }
  };

  const validCount = rows.filter(r => r.valid).length;
  const invalidCount = rows.length - validCount;

  return (
    <div>
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: PRIMARY }}>
            <TagsOutlined style={{ marginRight: 8, color: TEAL }} />Bulk Tag Provisioning
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Upload CSV/Excel of EPC tags + delegates · validates EPC format · batch creates delegates
          </Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<FileExcelOutlined />} onClick={downloadTemplate}>Download Template</Button>
            <Select value={eventId} onChange={setEventId} style={{ width: 220 }} placeholder="Event">
              {events.map(e => (
                <Option key={e.id} value={e.id}>
                  <Badge color={e.status === 'active' ? 'green' : 'default'} text={e.event_name} />
                </Option>
              ))}
            </Select>
          </Space>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16, borderRadius: 10 }}>
        <Steps current={step} size="small">
          <Step title="Upload File" icon={<UploadOutlined />} />
          <Step title="Review & Validate" icon={<SearchOutlined />} />
          <Step title="Provision" icon={<CloudUploadOutlined />} />
        </Steps>
      </Card>

      {step === 0 && (
        <Card style={{ borderRadius: 10 }}>
          <Upload.Dragger
            accept=".csv,.xlsx,.xls"
            showUploadList={false}
            beforeUpload={parseFile}
            multiple={false}
          >
            <p className="ant-upload-drag-icon"><CloudUploadOutlined style={{ fontSize: 60, color: TEAL }} /></p>
            <p className="ant-upload-text" style={{ fontSize: 16, fontWeight: 600 }}>
              Drag and drop your CSV/Excel here, or click to browse
            </p>
            <p className="ant-upload-hint">
              Required columns: <Text code>rfid_tag</Text> (EPC hex), <Text code>delegate_name</Text>.
              {' '}Optional: <Text code>category</Text>, <Text code>company</Text>, <Text code>email</Text>, <Text code>phone</Text>, <Text code>designation</Text>.
            </p>
          </Upload.Dragger>

          {/* Quick EPC decoder */}
          <Card size="small" style={{ marginTop: 16, background: '#0a0e1a' }} bodyStyle={{ padding: 14 }}>
            <Space style={{ width: '100%' }}>
              <Text style={{ color: '#94a3b8', fontFamily: 'Menlo, monospace', fontSize: 11 }}>EPC Inspector:</Text>
              <Input
                style={{ width: 320, fontFamily: 'Menlo, monospace', background: '#1e293b', color: '#10b981', border: '1px solid #334155' }}
                placeholder="Paste EPC hex (e.g., E28011700000020D2D2A1234)"
                value={decodeInput}
                onChange={e => setDecodeInput(e.target.value.toUpperCase())}
                onPressEnter={handleDecode}
              />
              <Button onClick={handleDecode} loading={decodeBusy} type="primary" style={{ background: TEAL, borderColor: TEAL }}>
                Decode
              </Button>
            </Space>
            {decoded && (
              <div style={{ marginTop: 12, padding: 10, background: '#11172a', borderRadius: 6, color: '#e2e8f0', fontFamily: 'Menlo, monospace', fontSize: 12 }}>
                {decoded.error ? (
                  <Text style={{ color: '#ef4444' }}>✘ {decoded.error}</Text>
                ) : (
                  <Space direction="vertical" size={2}>
                    <span>📡 EPC: <Text style={{ color: '#a78bfa' }}>{decoded.epc}</Text></span>
                    <span>📏 Length: {decoded.length_bits} bits ({decoded.length_hex} hex chars)</span>
                    <span>🏷  Header: 0x{decoded.header_hex} ({decoded.header_dec})</span>
                    <span>📋 Type: <Text style={{ color: '#10b981' }}>{decoded.header_meaning}</Text></span>
                    {decoded.gen      && <span>📻 Gen: {decoded.gen} · {decoded.frequency}</span>}
                    {decoded.serial_dec && <span>🔢 Serial: {decoded.serial_dec.toLocaleString()} (0x{decoded.serial_hex})</span>}
                  </Space>
                )}
              </div>
            )}
          </Card>
        </Card>
      )}

      {step === 1 && (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={8}>
              <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${PRIMARY}` }}>
                <Statistic title="Total Rows" value={rows.length} />
              </Card>
            </Col>
            <Col xs={8}>
              <Card size="small" style={{ borderRadius: 10, borderTop: '3px solid #10b981' }}>
                <Statistic title="Valid" value={validCount} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#10b981' }} />
              </Card>
            </Col>
            <Col xs={8}>
              <Card size="small" style={{ borderRadius: 10, borderTop: '3px solid #ef4444' }}>
                <Statistic title="Invalid" value={invalidCount} prefix={<WarningOutlined />} valueStyle={{ color: '#ef4444' }} />
              </Card>
            </Col>
          </Row>

          {invalidCount > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`${invalidCount} row(s) failed EPC or required-field validation`}
              description="Invalid rows will be skipped during provisioning. Fix and re-upload to include them."
              style={{ marginBottom: 12, borderRadius: 8 }}
            />
          )}

          <Card title={`Preview: ${filename}`} style={{ borderRadius: 10 }}
            extra={
              <Space>
                <Button icon={<RollbackOutlined />} onClick={reset}>Re-upload</Button>
                <Button type="primary" icon={<CloudUploadOutlined />} onClick={submit}
                  loading={submitting} disabled={validCount === 0}
                  style={{ background: TEAL, borderColor: TEAL }}>
                  Provision {validCount} Tags
                </Button>
              </Space>
            }>
            <Table
              dataSource={rows}
              rowKey="_row"
              size="small"
              pagination={{ pageSize: 10 }}
              rowClassName={r => r.valid ? '' : 'invalid-row'}
              columns={[
                { title: '#', dataIndex: '_row', width: 50 },
                { title: 'Status', width: 80,
                  render: (_, r) => r.valid
                    ? <Tag icon={<CheckCircleOutlined />} color="success">OK</Tag>
                    : <Tag icon={<WarningOutlined />} color="error">{r.error}</Tag>
                },
                { title: 'RFID Tag (EPC)', dataIndex: 'rfid_tag', width: 230,
                  render: v => <Text code style={{ fontSize: 10 }}>{v}</Text> },
                { title: 'Name', dataIndex: 'delegate_name' },
                { title: 'Category', dataIndex: 'category', width: 110,
                  render: v => v ? <Tag>{v}</Tag> : '-' },
                { title: 'Company', dataIndex: 'company', width: 150 },
                { title: 'Email', dataIndex: 'email', width: 160 },
              ]}
            />
            <style>{`.invalid-row{ background: #fff1f2 !important; }`}</style>
          </Card>
        </>
      )}

      {step === 2 && result && (
        <Card style={{ borderRadius: 10 }}>
          <Result
            status={result.summary.invalid > 0 || result.summary.skipped > 0 ? 'warning' : 'success'}
            icon={<CheckCircleOutlined style={{ color: TEAL }} />}
            title={`Provisioned ${result.summary.created} of ${result.summary.total} delegates`}
            subTitle={
              <Space size="large" wrap>
                <Tag color="success">✓ Created: {result.summary.created}</Tag>
                <Tag color="warning">⊘ Skipped (duplicate): {result.summary.skipped}</Tag>
                <Tag color="error">✘ Invalid: {result.summary.invalid}</Tag>
              </Space>
            }
            extra={[
              <Button key="again" type="primary" onClick={reset} style={{ background: TEAL, borderColor: TEAL }}>
                Provision More Tags
              </Button>,
            ]}
          />
          {result.errors?.length > 0 && (
            <Card size="small" title="Errors" style={{ marginTop: 16 }}>
              <Table
                dataSource={result.errors}
                rowKey={(_, i) => i}
                pagination={{ pageSize: 10 }}
                size="small"
                columns={[
                  { title: 'Row', dataIndex: 'row', width: 80 },
                  { title: 'Tag', dataIndex: 'value' },
                  { title: 'Reason', dataIndex: 'reason' },
                ]}
              />
            </Card>
          )}
        </Card>
      )}
    </div>
  );
}

// Tiny CSV parser supporting quoted fields with embedded commas
function parseCsvLine(line) {
  const out = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (c === ',' && !inQ) {
      out.push(cur); cur = '';
    } else cur += c;
  }
  out.push(cur);
  return out;
}
